"use client";

import Image from "next/image";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Box,
  Camera,
  CalendarDays,
  Check,
  ClipboardCheck,
  CreditCard,
  Globe2,
  Headphones,
  Link2,
  Play,
  Plane,
  ReceiptText,
  ShieldCheck,
  ShoppingBag,
  Warehouse,
} from "lucide-react";
import { HomeHeader } from "@/components/skin-lab/current/home-header";
import { SiteFooter } from "@/components/prv/site-footer";
import { productSearchDestination } from "@/lib/product-link";
import { readLocalePreferences } from "@/lib/locale-preferences";
import type { AuthSession } from "@/lib/auth-session";

const services = [
  {
    title: "Real Humans. Real Support.",
    label: "Private Agent",
    copy: "Every PRVBUY customer gets dedicated support from a real person. Your agent follows your order from purchase to delivery, helping with sourcing, QC updates, shipping questions and after-sales support.",
    icon: Headphones,
    tone: "lavender",
    bg: "private-agent-bg",
    photo: "/images/home-services-distinctive-v3/private-agent-real-support.png",
    photoPosition: "78% 50%",
    photoActivePosition: "50% 50%",
    photoMobilePosition: "82% 50%",
    visual: "search",
  },
  {
    title: "Secure Payments. Protected Purchases.",
    label: "Secure Payment",
    copy: "Pay securely with PayPal or credit card. All transactions are protected through SSL encryption and trusted payment providers. PRVBUY never stores your payment information, and all card payments require 3D Secure verification.",
    icon: CreditCard,
    tone: "yellow",
    bg: "secure-payment-bg",
    photo: "/images/home-services-distinctive-v3/secure-payment-landscape.png",
    photoPosition: "68% 62%",
    photoActivePosition: "50% 50%",
    visual: "purchase",
  },
  {
    title: "100% Transparent Purchasing",
    label: "Transparent Purchase",
    copy: "Original purchase records, supplier details and payment proof for every order. What you see is what we buy.",
    icon: ReceiptText,
    tone: "blue",
    bg: "transparent-purchase-bg",
    photo: "/images/home-services-distinctive-v3/purchasing-platforms.png",
    photoPosition: "74% 42%",
    photoActivePosition: "50% 50%",
    photoMobilePosition: "70% 50%",
    photoActiveSize: "cover",
    visual: "parcel",
  },
  {
    title: "See Every Detail Clearly",
    label: "Professional QC",
    copy: "Our trained QC team follows standardized inspection and photography procedures. See every detail before your parcel leaves China.",
    icon: ShieldCheck,
    tone: "mint",
    bg: "professional-qc-bg",
    photo: "/images/home-services-distinctive-v3/product-quality-inspection.png",
    photoPosition: "72% 50%",
    photoActivePosition: "50% 50%",
    photoMobilePosition: "68% 50%",
    photoActiveSize: "cover",
    visual: "qc",
  },
  {
    title: "Store Longer. Ship Smarter.",
    label: "Storage & Consolidation",
    copy: "Store items for up to 180 days and combine multiple purchases into one optimized parcel before shipping.",
    icon: CalendarDays,
    tone: "pink",
    bg: "storage-consolidation-bg",
    photo: "/images/home-services-distinctive-v3/warehouse-receiving-storage-v1.png",
    photoPosition: "76% 50%",
    photoActivePosition: "50% 50%",
    photoMobilePosition: "66% 50%",
    photoActiveSize: "cover",
    visual: "storage",
  },
  {
    title: "Global Shipping. Made Simple.",
    label: "Global Shipping",
    copy: "Choose the route that fits your budget and delivery timeline. Transparent pricing, reliable carriers and worldwide delivery.",
    icon: Plane,
    tone: "green",
    bg: "global-shipping-bg",
    photo: "/images/home-services-distinctive-v3/international-flight-dispatch-v2.png",
    photoPosition: "78% 50%",
    photoActivePosition: "50% 50%",
    photoMobilePosition: "62% 50%",
    photoActiveSize: "cover",
    visual: "shipping",
  },
];

const heroStats = [
  ["180 Days", "Free Storage"],
  ["24/7", "Human Support"],
  ["24 Hours", "Avg. Purchase Time"],
  ["100%", "Purchase Transparency"],
];

const platformRibbonItems = [
  {
    key: "weidian",
    label: "Weidian",
    caption: "Micro stores sourced by PRVBUY",
    icon: "/images/skin-lab/platform-ribbon/stickers/weidian-icon.png",
    word: "/images/skin-lab/platform-ribbon/stickers/weidian-word.png",
    tone: "red",
  },
  {
    key: "1688",
    label: "1688",
    caption: "Factory deals handled by agents",
    icon: "/images/skin-lab/platform-ribbon/stickers/1688-icon.png",
    word: "/images/skin-lab/platform-ribbon/stickers/1688-word.png",
    tone: "orange",
  },
  {
    key: "taobao",
    label: "Taobao",
    caption: "Taobao links, checked and shipped",
    icon: "/images/skin-lab/platform-ribbon/stickers/taobao-icon.png",
    word: "/images/skin-lab/platform-ribbon/stickers/taobao-word.png",
    tone: "taobao",
  },
  {
    key: "xianyu",
    label: "Xianyu",
    caption: "Secondhand finds, verified first",
    icon: "/images/skin-lab/platform-ribbon/stickers/xianyu-icon.png",
    word: "/images/skin-lab/platform-ribbon/stickers/xianyu-word.png",
    tone: "yellow",
  },
] as const;

const stats = [
  { baseValue: 128000, dailyGrowth: 200, warmupOffset: 80, label: "Orders Processed", tone: "gold", format: "compactK" },
  { baseValue: 510000, dailyGrowth: 800, warmupOffset: 320, label: "QC Photos Taken", tone: "mint", format: "compactK" },
  { baseValue: 245, dailyGrowth: 0, warmupOffset: 0, label: "Countries Served", tone: "gold", format: "plain" },
  { baseValue: 178000, dailyGrowth: 200, warmupOffset: 80, label: "Parcels Delivered", tone: "mint", format: "compactK" },
];

const statsBaseDate = new Date("2026-06-24T00:00:00+08:00").getTime();

function getLiveStatValue(baseValue: number, dailyGrowth: number) {
  const elapsedDays = Math.max(0, (Date.now() - statsBaseDate) / 86_400_000);
  return baseValue + Math.floor(elapsedDays * dailyGrowth);
}

function AnimatedStatNumber({
  baseValue,
  dailyGrowth,
  warmupOffset,
  format,
}: {
  baseValue: number;
  dailyGrowth: number;
  warmupOffset: number;
  format: "compactK" | "plain";
}) {
  const [displayValue, setDisplayValue] = useState(() => getLiveStatValue(baseValue, dailyGrowth));
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targetValue = getLiveStatValue(baseValue, dailyGrowth);
    if (prefersReducedMotion) return;

    let frame = 0;
    let liveTimer = 0;
    let started = false;
    const animate = () => {
      if (!warmupOffset || dailyGrowth === 0) return;
      const duration = 7200;
      const start = performance.now();
      const startValue = Math.max(0, targetValue - warmupOffset);
      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplayValue(Math.round(startValue + (targetValue - startValue) * eased));
        if (progress < 1) frame = window.requestAnimationFrame(tick);
        else if (dailyGrowth > 0) liveTimer = window.setInterval(() => setDisplayValue(getLiveStatValue(baseValue, dailyGrowth)), 30_000);
      };
      frame = window.requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || started) return;
      started = true;
      animate();
      observer.disconnect();
    }, { threshold: 0.35 });
    observer.observe(node);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      window.clearInterval(liveTimer);
    };
  }, [baseValue, dailyGrowth, warmupOffset]);

  const formattedValue = format === "compactK"
    ? `${Math.round(displayValue / 1000).toLocaleString("en-US")}K`
    : displayValue.toLocaleString("en-US");
  return (
    <span ref={ref} className="stat-number">
      {formattedValue}<span className="stat-plus">+</span>
    </span>
  );
}

const processSteps = [
  {
    number: "01",
    title: "Paste Product Link",
    copy: "Submit a Taobao, 1688, Weidian or Yupoo product link.",
    icon: Link2,
  },
  {
    number: "02",
    title: "Select & Pay",
    copy: "Choose product options and pay for your order.",
    icon: CreditCard,
  },
  {
    number: "03",
    title: "Agent Purchase",
    copy: "Your PRVBUY agent purchases the item on your behalf.",
    icon: ShoppingBag,
  },
  {
    number: "04",
    title: "QC Inspection",
    copy: "Review professional QC photos before shipping.",
    icon: Camera,
  },
  {
    number: "05",
    title: "Submit Shipping",
    copy: "Choose your shipping route and complete delivery payment.",
    icon: Globe2,
  },
  {
    number: "06",
    title: "Global Delivery",
    copy: "Track your parcel from dispatch to delivery.",
    icon: Plane,
  },
];

const story = [
  { label: "Product", icon: ShoppingBag },
  { label: "Purchase", icon: Check },
  { label: "Warehouse", icon: Warehouse },
  { label: "QC", icon: Camera },
  { label: "Packaging", icon: Box },
  { label: "Delivery", icon: Plane },
];

const heroPlatformNodes = [
  { label: "Taobao", logo: "/images/footer/agent-taobao.svg", className: "platform-taobao" },
  { label: "1688", logo: "/images/footer/agent-1688.svg", className: "platform-1688" },
  { label: "Weidian", logo: "/images/footer/agent-weidian.svg", className: "platform-weidian" },
  { label: "Yupoo", logo: "/images/footer/agent-yupoo.svg", className: "platform-yupoo" },
  { label: "Xianyu", logo: "/images/footer/agent-xianyu.svg", className: "platform-xianyu" },
];

// Replace this with the final PRVBUY YouTube guide video id when it is ready.
const guideVideoId = "";

function useReveal() {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className={compact ? "brand-mark compact" : "brand-mark"} aria-hidden="true">
      <span />
    </span>
  );
}

function HeroWorkflow() {
  return (
    <div className="hero-visual" aria-label="PRVBUY product to delivery workflow">
      <div className="cloud cloud-one" />
      <div className="cloud cloud-two" />
      <div className="workflow-photo">
        <Image
          src="/images/hero-prvbuy-workspace.png"
          alt=""
          width={1800}
          height={964}
          priority
          sizes="(max-width: 640px) 520px, (max-width: 1024px) 720px, 980px"
        />
      </div>

      <div className="workflow-rail">
        {story.map((item, index) => {
          const Icon = item.icon;
          return (
            <div className="workflow-node" style={{ "--i": index } as CSSProperties} key={item.label}>
              <span>
                <Icon size={18} />
              </span>
              <strong>{item.label}</strong>
            </div>
          );
        })}
      </div>

      <div className="floating-panel link-panel">
        <Link2 size={18} />
        <div>
          <strong>Product Link Parsed</strong>
          <span>Taobao · 1688 · Weidian supported</span>
        </div>
      </div>

      <div className="floating-panel qc-panel">
        <ClipboardCheck size={18} />
        <div>
          <strong>QC Inspection Ready</strong>
          <span>Clear photos before international shipping</span>
        </div>
      </div>

      <div className="floating-panel route-panel">
        <ShoppingBag size={18} />
        <div>
          <strong>Dedicated Agent Assigned</strong>
          <span>Real human support from purchase to delivery</span>
        </div>
      </div>
    </div>
  );
}

function DynamicHeroBackground() {
  return (
    <div className="hero-dynamic-bg" aria-hidden="true">
      <div className="hero-noise" />
      <div className="hero-photo-layer">
        <Image
          src="/images/hero/prvbuy-cross-border-studio-hero-logo-clean-4k-v4.png"
          alt=""
          width={3840}
          height={2160}
          priority
          sizes="100vw"
        />
      </div>
    </div>
  );
}

function PlatformRibbon({
  platform,
  reverse = false,
}: {
  platform: (typeof platformRibbonItems)[number]["key"];
  reverse?: boolean;
}) {
  const item = platformRibbonItems.find((ribbonItem) => ribbonItem.key === platform) ?? platformRibbonItems[0];
  const ribbonItems = Array.from({ length: 5 }, () => item);

  return (
    <div className={`platform-ribbon platform-ribbon-${item.tone}`} aria-hidden="true">
      <div className={reverse ? "platform-ribbon-track reverse" : "platform-ribbon-track"}>
        {[0, 1].map((group) => (
          <span className="platform-ribbon-group" key={`${item.key}-${group}`}>
            {ribbonItems.map((ribbonItem, index) => (
              <span
                className={`platform-ribbon-unit platform-ribbon-unit-${ribbonItem.tone}`}
                key={`${ribbonItem.key}-${group}-${index}`}
              >
                <Image
                  className="platform-ribbon-icon"
                  src={ribbonItem.icon}
                  alt=""
                  width={1024}
                  height={1024}
                  sizes="(max-width: 760px) 82px, 148px"
                  loading="lazy"
                />
                <span className="platform-ribbon-copy">
                  <Image
                    className="platform-ribbon-word"
                    src={ribbonItem.word}
                    alt={ribbonItem.label}
                    width={1600}
                    height={500}
                    sizes="(max-width: 760px) 210px, 390px"
                    loading="lazy"
                  />
                  <span className="platform-ribbon-caption">{ribbonItem.caption}</span>
                </span>
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
}

function ServiceArtwork({ type }: { type: string }) {
  return (
    <div className={`service-art ${type}`} aria-hidden="true">
      <span className="art-desk" />
      <span className="art-box box-a" />
      <span className="art-box box-b" />
      <span className="art-box box-c" />
      <span className="art-label label-a" />
      <span className="art-label label-b" />
      <span className="art-scan" />
      <span className="art-plane" />
    </div>
  );
}

function GuideVideo() {
  if (guideVideoId) {
    return (
      <iframe
        title="PRVBUY guide"
        src={`https://www.youtube.com/embed/${guideVideoId}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <div className="guide-poster">
      <div className="guide-poster-image">
        <Image
          src="/images/hero-prvbuy-workspace.png"
          alt="PRVBUY warehouse guide preview"
          width={1800}
          height={964}
          sizes="(max-width: 640px) 320px, 720px"
        />
      </div>
      <a
        className="guide-play"
        href="https://www.youtube.com/@PRVBUY"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open PRVBUY tutorials on YouTube"
      >
        <Play size={34} fill="currentColor" />
      </a>
      <div className="guide-caption">
        <strong>How PRVBUY Works</strong>
        <span>Paste link. QC check. Ship worldwide.</span>
      </div>
    </div>
  );
}

export default function Home({ initialSession }: { initialSession: AuthSession | null }) {
  const [activeService, setActiveService] = useState(0);
  const [searchHot, setSearchHot] = useState(false);
  const [heroSearch, setHeroSearch] = useState("");
  useReveal();

  useEffect(() => {
    const syncScaleGap = () => {
      const page = document.querySelector<HTMLElement>(".cd-page");
      if (!page || !window.matchMedia("(min-width: 1200px)").matches) {
        document.documentElement.style.removeProperty("--home-scale-gap");
        return;
      }

      const scale = 0.8;
      const gap = Math.max(0, Math.round(page.offsetHeight * (1 - scale)));
      document.documentElement.style.setProperty("--home-scale-gap", `${gap}px`);
    };

    syncScaleGap();
    const frame = window.requestAnimationFrame(syncScaleGap);
    const observer =
      "ResizeObserver" in window ? new ResizeObserver(syncScaleGap) : null;
    const page = document.querySelector<HTMLElement>(".cd-page");

    if (page) {
      observer?.observe(page);
    }

    window.addEventListener("resize", syncScaleGap);
    window.addEventListener("load", syncScaleGap);

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", syncScaleGap);
      window.removeEventListener("load", syncScaleGap);
      document.documentElement.style.removeProperty("--home-scale-gap");
    };
  }, []);

  const openMobileSearch = () => {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches) {
      window.dispatchEvent(
        new CustomEvent("prvbuy:open-mobile-search", { detail: { mode: "product" } }),
      );
    }
  };

  const submitHeroSearch = () => {
    const query = heroSearch.trim();
    if (!query) return;
    window.location.assign(productSearchDestination(query, readLocalePreferences().language));
  };

  useEffect(() => {
    const footer = document.querySelector<HTMLElement>(".footer-section");
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        document.body.classList.toggle("prvbuy-home-footer-visible", entry.isIntersecting);
      },
      { threshold: 0.08 },
    );

    observer.observe(footer);

    return () => {
      observer.disconnect();
      document.body.classList.remove("prvbuy-home-footer-visible");
    };
  }, []);

  return (
    <>
      <HomeHeader initialSession={initialSession} linkPlaceholder="Enter the link or keyword" />

      <main className="cd-page">

      <section className={searchHot ? "hero-section search-active" : "hero-section"} id="home">
        <DynamicHeroBackground />
        <div
          className="hero-copy"
          style={{
            width: "min(1200px, calc(100vw - 56px))",
            maxWidth: "1200px",
            transform: "translateY(10px)",
          }}
        >
          <h1
            style={{
              fontSize: "clamp(78px, 6.1vw, 116px)",
              lineHeight: 1.03,
              letterSpacing: 0,
            }}
          >
            <span>Shop China</span>
            <span>Like A Local</span>
          </h1>
          <p
            style={{
              maxWidth: "820px",
              marginTop: "42px",
              fontSize: "clamp(20px, 1.2vw, 24px)",
              lineHeight: 1.5,
              animation: "none",
              transform: "none",
            }}
          >
            Your private buying agent in China.
            <br />
            Purchase, inspect, store and ship — all managed for you.
          </p>
          <div className="hero-trust-tags hero-proof-strip" aria-label="PRVBUY service standards">
            {heroStats.map(([value, label]) => (
              <span key={label} style={{ animation: "none", transform: "none" }}>
                <strong>{value}</strong>
                <em>{label}</em>
              </span>
            ))}
          </div>
          <form
            className="hero-action"
            role="search"
            action="/product/search"
            method="get"
            onMouseEnter={() => setSearchHot(true)}
            onMouseLeave={() => setSearchHot(false)}
            onFocus={() => setSearchHot(true)}
            onBlur={() => setSearchHot(false)}
            onSubmit={(event) => {
              event.preventDefault();
              submitHeroSearch();
            }}
            style={{
              width: "min(1060px, 100%)",
              minHeight: "94px",
              marginTop: "50px",
              padding: "12px",
              animation: "none",
              transform: "none",
            }}
          >
            <div
              className="link-input mobile-search-trigger"
              style={{ gap: "16px", paddingLeft: "28px" }}
            >
              <Link2 size={22} />
              <input
                className="hero-placeholder"
                type="text"
                name="q"
                value={heroSearch}
                onChange={(event) => setHeroSearch(event.target.value)}
                onFocus={openMobileSearch}
                onClick={openMobileSearch}
                placeholder="Enter the link or keyword"
                aria-label="Enter the link or keyword"
                style={{ maxWidth: "none", fontSize: "clamp(18px, 1.08vw, 21px)" }}
              />
              <span
                className="search-platform-hints"
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "12px",
                  maxWidth: "none",
                  marginLeft: "auto",
                  paddingRight: "20px",
                  overflow: "visible",
                }}
              >
                <Image
                  src="/images/hero-platform-1688-badge.svg"
                  alt=""
                  width={44}
                  height={44}
                  style={{ width: 44, height: 44 }}
                />
                <Image
                  src="/images/hero-platform-taobao-badge.svg"
                  alt=""
                  width={44}
                  height={44}
                  style={{ width: 44, height: 44 }}
                />
                <Image
                  src="/images/hero-platform-weidian-badge.svg"
                  alt=""
                  width={44}
                  height={44}
                  style={{ width: 44, height: 44 }}
                />
              </span>
            </div>
            <button className="primary-pill" type="submit">
              Start Shopping
            </button>
          </form>
          <a className="secondary-action" href="#process">
            <span>See how PRVBUY works</span>
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <PlatformRibbon platform="weidian" />

      <section className="services-section" id="services">
        <div className="wide-head" data-reveal>
          <p>THE PRVBUY DIFFERENCE</p>
          <h2>Your Team In China.</h2>
          <span>
            From purchasing and quality control to warehousing and international shipping,
            every step is managed for you.
          </span>
        </div>

        <div className="service-accordion" data-reveal>
          {services.map((service, index) => {
            const Icon = service.icon;
            const isActive = activeService === index;
            return (
              <button
                className={`service-card ${service.tone} ${service.bg} ${service.photo ? "has-photo-bg" : ""} ${
                  isActive ? "active" : ""
                }`}
                type="button"
                aria-expanded={isActive}
                onClick={() => setActiveService(index)}
                onMouseEnter={() => setActiveService(index)}
                style={
                  service.photo
                    ? ({
                        "--service-photo": `url("${service.photo}")`,
                        "--service-photo-position": service.photoPosition,
                        "--service-photo-active-position":
                          "photoActivePosition" in service ? service.photoActivePosition : "center",
                        "--service-photo-mobile-position":
                          "photoMobilePosition" in service
                            ? service.photoMobilePosition
                            : "photoActivePosition" in service
                              ? service.photoActivePosition
                              : "center",
                        "--service-photo-active-size":
                          "photoActiveSize" in service ? service.photoActiveSize : "cover",
                      } as CSSProperties)
                    : undefined
                }
                key={service.title}
              >
                <div className="service-icon">
                  <Icon size={32} />
                </div>
                <div className="service-active-content">
                  <h3>{service.title}</h3>
                  <p>{service.copy}</p>
                </div>
                <strong className="service-rail-label">{service.label}</strong>
                <ServiceArtwork type={service.visual} />
              </button>
            );
          })}
        </div>
      </section>

      <PlatformRibbon platform="1688" reverse />

      <section className="process-section" id="process">
        <div className="how-grid" data-reveal>
          <div className="how-copy">
            <span className="how-eyebrow">How it works</span>
            <h2>
              Buy From China
              <strong>In 6 Simple Steps</strong>
            </h2>
            <p>Paste a product link, approve QC photos, consolidate your items and ship worldwide with PRVBUY.</p>
            <div className="how-actions">
              <a className="guide-cta primary" href="#home">
                Start Shopping
                <ArrowRight size={18} />
              </a>
              <a
                className="guide-cta ghost"
                href="https://www.youtube.com/@PRVBUY"
                target="_blank"
                rel="noopener noreferrer"
              >
                Watch Demo
              </a>
            </div>
          </div>

          <div
            className="guide-video-card"
            onMouseMove={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              event.currentTarget.style.setProperty("--mx", `${((event.clientX - rect.left) / rect.width - 0.5) * 18}px`);
              event.currentTarget.style.setProperty("--my", `${((event.clientY - rect.top) / rect.height - 0.5) * 18}px`);
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.setProperty("--mx", "0px");
              event.currentTarget.style.setProperty("--my", "0px");
            }}
          >
            <GuideVideo />
          </div>
        </div>

        <div className="how-steps" data-reveal aria-label="PRVBUY buying process">
          {processSteps.map((step) => {
            const StepIcon = step.icon;
            return (
              <article className="how-step-card" key={step.number}>
                <div className="how-step-top">
                  <span className="how-step-number">{step.number}</span>
                  <span className="how-step-icon">
                    <StepIcon size={18} />
                  </span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </article>
            );
          })}
        </div>

      </section>

      <PlatformRibbon platform="taobao" />

      <section className="momentum-section" id="stats">
        <div className="momentum-shell" data-reveal>
          <div className="momentum-head">
            <div>
              <span className="momentum-kicker">PRVBUY in motion</span>
              <h2>See Every Detail Before It Ships.</h2>
            </div>
          </div>

          <div className="momentum-body">
            <div className="stats-grid" aria-label="PRVBUY operating metrics">
              {stats.map((stat) => (
                <article className={`stats-card ${stat.tone}`} key={stat.label}>
                  <strong>
                    <AnimatedStatNumber
                      baseValue={stat.baseValue}
                      dailyGrowth={stat.dailyGrowth}
                      warmupOffset={stat.warmupOffset}
                      format={stat.format as "compactK" | "plain"}
                    />
                  </strong>
                  <span>{stat.label}</span>
                </article>
              ))}
            </div>
          </div>

        </div>
      </section>

      <PlatformRibbon platform="xianyu" reverse />

      <SiteFooter />
      </main>
    </>
  );
}
